<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CaptainAssistant from 'dashboard/api/captain/assistant';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['applied']);

const { t } = useI18n();
const store = useStore();
const packs = ref([]);
const preview = ref(null);
const isLoadingList = ref(false);
const isLoadingPreview = ref(false);
const isApplying = ref(false);
const dialogRef = ref(null);

const assistantId = computed(() => Number(props.assistant?.id));

const packName = pack => {
  const key = (pack?.key || '').toUpperCase();
  const i18nKey = `CAPTAIN.PACKS.ITEMS.${key}.NAME`;
  const label = t(i18nKey);
  return label === i18nKey ? pack?.name || pack?.key : label;
};

const packSummary = pack => {
  const key = (pack?.key || '').toUpperCase();
  const i18nKey = `CAPTAIN.PACKS.ITEMS.${key}.SUMMARY`;
  const label = t(i18nKey);
  return label === i18nKey ? pack?.summary || '' : label;
};

const formatValue = value => {
  if (typeof value === 'boolean') {
    return value
      ? t('CAPTAIN.PACKS.PREVIEW.YES')
      : t('CAPTAIN.PACKS.PREVIEW.NO');
  }
  if (value === undefined || value === null || value === '') return '—';
  return String(value);
};

const behaviorRows = computed(() => {
  const behavior = preview.value?.behavior || {};
  return [
    ['SALES_STYLE', behavior.sales_style],
    ['PHOTO', behavior.photo_after_match],
    ['ASK_ORDER', behavior.ask_order_after_quote],
    ['ALLOW_CONFIRM', behavior.allow_confirm_order],
    ['LANGUAGE', behavior.default_language],
    ['LANGUAGE_MODE', behavior.language_mode],
  ].filter(row => row[1] !== undefined && row[1] !== null && row[1] !== '');
});

const filledScripts = computed(() => {
  const scripts = preview.value?.scripts || {};
  return Object.entries(scripts).filter(([, value]) => String(value).trim());
});

const scriptLabel = key => {
  const token = key.replace('ask_extra_', '').toUpperCase();
  const i18nKey = `CAPTAIN.ASSISTANTS.FORM.TRAINING.SCRIPTS.${token}.LABEL`;
  const label = t(i18nKey);
  if (label !== i18nKey) return label;
  if (key.startsWith('ask_extra_')) {
    return t('CAPTAIN.ASSISTANTS.FORM.TRAINING.SCRIPTS.ASK_EXTRA.LABEL', {
      slot: key.replace('ask_extra_', ''),
    });
  }
  return key;
};

const extraSlotLabels = computed(() =>
  (preview.value?.extra_slots || [])
    .map(slot => slot.label || slot.key)
    .filter(Boolean)
    .join(', ')
);

const overwriteCopy = computed(() => {
  const kind = preview.value?.overwrite_kind;
  if (kind === 'same') return t('CAPTAIN.PACKS.OVERWRITE.SAME');
  if (kind === 'other') return t('CAPTAIN.PACKS.OVERWRITE.OTHER');
  return t('CAPTAIN.PACKS.OVERWRITE.FRESH');
});

const loadPacks = async () => {
  if (!assistantId.value) return;
  isLoadingList.value = true;
  try {
    const { data } = await CaptainAssistant.listSalesPacks(assistantId.value);
    packs.value = Array.isArray(data?.payload) ? data.payload : [];
  } catch (error) {
    packs.value = [];
    useAlert(error?.message || t('CAPTAIN.PACKS.LOAD_ERROR'));
  } finally {
    isLoadingList.value = false;
  }
};

const openPreview = async pack => {
  if (!assistantId.value || !pack?.key) return;
  isLoadingPreview.value = true;
  preview.value = null;
  dialogRef.value?.open();
  try {
    const { data } = await CaptainAssistant.previewSalesPack({
      assistantId: assistantId.value,
      packKey: pack.key,
    });
    preview.value = data;
  } catch (error) {
    dialogRef.value?.close();
    useAlert(error?.message || t('CAPTAIN.PACKS.LOAD_ERROR'));
  } finally {
    isLoadingPreview.value = false;
  }
};

const applyPack = async () => {
  if (!assistantId.value || !preview.value?.key || isApplying.value) return;
  isApplying.value = true;
  try {
    await CaptainAssistant.applySalesPack({
      assistantId: assistantId.value,
      packKey: preview.value.key,
    });
    await store.dispatch('captainAssistants/show', assistantId.value);
    useAlert(t('CAPTAIN.PACKS.APPLY_SUCCESS'));
    dialogRef.value?.close();
    emit('applied');
    await loadPacks();
  } catch (error) {
    const apiMessage = error?.response?.data?.error;
    useAlert(apiMessage || error?.message || t('CAPTAIN.PACKS.APPLY_ERROR'));
  } finally {
    isApplying.value = false;
  }
};

const closePreview = () => {
  preview.value = null;
};

watch(assistantId, loadPacks, { immediate: true });
</script>

<template>
  <section
    class="flex flex-col gap-4 rounded-xl outline outline-1 outline-n-weak p-4"
  >
    <div class="flex flex-col gap-1">
      <h4 class="mb-0 text-base font-medium text-n-slate-12">
        {{ t('CAPTAIN.PACKS.TITLE') }}
      </h4>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('CAPTAIN.PACKS.HELP') }}
      </p>
    </div>
    <p v-if="isLoadingList" class="mb-0 text-sm text-n-slate-11">
      {{ t('CAPTAIN.PACKS.LOADING') }}
    </p>
    <div v-else class="grid grid-cols-1 gap-3 md:grid-cols-2">
      <article
        v-for="pack in packs"
        :key="pack.key"
        class="flex flex-col gap-3 rounded-xl bg-n-alpha-black2 p-4"
      >
        <div class="flex items-start justify-between gap-2">
          <h5 class="mb-0 text-sm font-medium text-n-slate-12">
            {{ packName(pack) }}
          </h5>
          <span
            v-if="pack.applied"
            class="rounded-full bg-n-solid-iris px-2 py-0.5 text-xs text-n-slate-12"
          >
            {{ t('CAPTAIN.PACKS.APPLIED_BADGE') }}
          </span>
        </div>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ packSummary(pack) }}
        </p>
        <Button
          faded
          sm
          :label="t('CAPTAIN.PACKS.PREVIEW_BUTTON')"
          @click="openPreview(pack)"
        />
      </article>
    </div>

    <Dialog
      ref="dialogRef"
      type="edit"
      width="2xl"
      overflow-y-auto
      :title="preview ? packName(preview) : t('CAPTAIN.PACKS.PREVIEW.TITLE')"
      :description="overwriteCopy"
      :confirm-button-label="t('CAPTAIN.PACKS.APPLY')"
      :disable-confirm-button="!preview || isLoadingPreview || isApplying"
      :is-loading="isApplying"
      @confirm="applyPack"
      @close="closePreview"
    >
      <div v-if="isLoadingPreview" class="text-sm text-n-slate-11">
        {{ t('CAPTAIN.PACKS.LOADING') }}
      </div>
      <div v-else-if="preview" class="flex flex-col gap-4">
        <p class="mb-0 text-sm text-n-slate-11">
          {{ packSummary(preview) }}
        </p>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ t('CAPTAIN.PACKS.KEEPS') }}
        </p>

        <div v-if="behaviorRows.length" class="flex flex-col gap-1">
          <h5 class="mb-0 text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.PACKS.PREVIEW.BEHAVIOR') }}
          </h5>
          <ul class="mb-0 list-disc pl-5 text-sm text-n-slate-12">
            <li v-for="[labelKey, value] in behaviorRows" :key="labelKey">
              {{ t(`CAPTAIN.PACKS.PREVIEW.FIELDS.${labelKey}`) }}:
              {{ formatValue(value) }}
            </li>
          </ul>
        </div>

        <div v-if="preview.business_type" class="flex flex-col gap-1">
          <h5 class="mb-0 text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.PACKS.PREVIEW.BRAIN') }}
          </h5>
          <p class="mb-0 text-sm text-n-slate-12">
            {{ t('CAPTAIN.PACKS.PREVIEW.BUSINESS_TYPE') }}:
            {{ preview.business_type }}
          </p>
          <p
            v-if="preview.extra_slots?.length"
            class="mb-0 text-sm text-n-slate-12"
          >
            {{ t('CAPTAIN.PACKS.PREVIEW.SLOTS') }}:
            {{ extraSlotLabels }}
          </p>
          <p v-else class="mb-0 text-sm text-n-slate-11">
            {{ t('CAPTAIN.PACKS.PREVIEW.NO_EXTRA_SLOTS') }}
          </p>
        </div>

        <div v-if="filledScripts.length" class="flex flex-col gap-2">
          <h5 class="mb-0 text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.PACKS.PREVIEW.SCRIPTS') }}
          </h5>
          <div
            v-for="[key, value] in filledScripts"
            :key="key"
            class="flex flex-col gap-1"
          >
            <p class="mb-0 text-xs font-medium text-n-slate-11">
              {{ scriptLabel(key) }}
            </p>
            <p class="mb-0 whitespace-pre-wrap text-sm text-n-slate-12">
              {{ value }}
            </p>
          </div>
        </div>

        <div v-if="preview.example_chats?.length" class="flex flex-col gap-3">
          <h5 class="mb-0 text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.PACKS.PREVIEW.EXAMPLES') }}
          </h5>
          <article
            v-for="chat in preview.example_chats"
            :key="chat.id"
            class="flex flex-col gap-1 rounded-lg bg-n-alpha-black2 p-3"
          >
            <p class="mb-0 text-sm font-medium text-n-slate-12">
              {{ chat.title }}
            </p>
            <p class="mb-0 text-sm text-n-slate-12">
              {{ t('CAPTAIN.PACKS.PREVIEW.CUSTOMER') }}: {{ chat.customer }}
            </p>
            <p class="mb-0 text-sm text-n-ruby-9">
              {{ t('CAPTAIN.PACKS.PREVIEW.BAD') }}: {{ chat.bad }}
            </p>
            <p class="mb-0 text-sm text-n-teal-11">
              {{ t('CAPTAIN.PACKS.PREVIEW.GOOD') }}: {{ chat.good }}
            </p>
          </article>
        </div>
      </div>
    </Dialog>
  </section>
</template>
